import { Injectable } from '@angular/core';
import { CompanyStorage } from 'src/app/_core/company-storage';
import {
  LastShiftTotalsResponse,
  TransactionsRegistrarStateResponse,
  isOfflineSequenceActive,
} from '../models/tax.model';
import { xml2json } from 'src/app/utility/xml-utils';

const TAX_REGISTER_DATA_KEY_PREFIX = 'TAX_REGISTER_DATA__';
const TAX_REGISTER_DOCUMENTS_KEY_PREFIX = 'TAX_REGISTER_DOCUMENTS__';
const TAX_REGISTER_SERVER_STATE_KEY_PREFIX = 'TAX_REGISTER_SERVER_STATE__';

export type TaxRegisterId = string | number;

export interface TaxServerState {
  UID: string;
  Timestamp: string;
}

export interface StoredTaxServerState {
  response: TaxServerState;
  currentTime: string;
}

export interface TaxRegisterState extends TransactionsRegistrarStateResponse {
  OfflineSession: boolean;
  Offline: boolean;
}

export interface TaxRegisterStoredData {
  state?: TransactionsRegistrarStateResponse;
  lastShiftTotals?: LastShiftTotalsResponse;
}

interface TaxDocumentHead {
  ORDERNUM: number | string;
  ORDERTAXNUM?: string;
  DOCTYPE?: number | string;
  OFFLINE?: boolean | string;
  TESTING?: boolean | string;
}

interface ParsedTaxDocument {
  CHECK?: {
    CHECKHEAD?: TaxDocumentHead;
  };
  ZREP?: {
    ZREPHEAD?: TaxDocumentHead;
  };
}

@Injectable({ providedIn: 'root' })
export class TaxRegisterV2Service {
  private _serverStates = new Map<string, StoredTaxServerState>();

  constructor(private _companyStorage: CompanyStorage) {}

  async saveServerState(
    taxRegisterId: TaxRegisterId,
    serverState: TaxServerState,
  ): Promise<void> {
    this._getTimestamp(serverState?.Timestamp, 'Server timestamp');

    const id = this._normalizeTaxRegisterId(taxRegisterId);
    const storedServerState: StoredTaxServerState = {
      response: serverState,
      currentTime: new Date(Date.now()).toISOString(),
    };

    await this._companyStorage.set(
      this._getServerStateStorageKey(id),
      storedServerState,
    );
    this._serverStates.set(id, storedServerState);
  }

  async getServerTime(taxRegisterId: TaxRegisterId): Promise<Date | null> {
    const storedServerState = await this._getStoredServerState(taxRegisterId);
    if (!storedServerState) {
      throw `ServerState not found. TaxRegister fiscal number ${taxRegisterId}`;
    }

    const serverResponseTime = this._getTimestamp(
      storedServerState.response.Timestamp,
      'Stored server timestamp',
    );
    const storedCurrentTime = this._getTimestamp(
      storedServerState.currentTime,
      'Stored current time',
    );
    const serverTimeDifference = serverResponseTime - storedCurrentTime;

    return new Date(Date.now() + serverTimeDifference);
  }

  private async _getStoredServerState(
    taxRegisterId: TaxRegisterId,
  ): Promise<StoredTaxServerState | null> {
    const id = this._normalizeTaxRegisterId(taxRegisterId);
    const cachedServerState = this._serverStates.get(id);
    if (cachedServerState) {
      return cachedServerState;
    }

    const storedServerState = await this._companyStorage.get(
      this._getServerStateStorageKey(id),
    );
    if (!storedServerState) {
      return null;
    }

    this._serverStates.set(id, storedServerState);
    return storedServerState;
  }

  async saveState(
    taxRegisterId: TaxRegisterId,
    state: TransactionsRegistrarStateResponse,
  ): Promise<void> {
    return this._saveTaxRegisterData(taxRegisterId, { state });
  }

  async getState(
    taxRegisterId: TaxRegisterId,
  ): Promise<TransactionsRegistrarStateResponse | null> {
    const data = await this._getTaxRegisterData(taxRegisterId);
    return data?.state || null;
  }

  async getTaxRegisterState(
    taxRegisterId: TaxRegisterId,
  ): Promise<TaxRegisterState> {
    const storedData = await this._getTaxRegisterData(taxRegisterId);
    const documents = await this.getDocuments(taxRegisterId);
    return this._calculateTaxRegisterState(
      taxRegisterId,
      storedData,
      documents,
    );
  }

  async saveStateAndLastShiftTotals(
    taxRegisterId: TaxRegisterId,
    state: TransactionsRegistrarStateResponse,
    lastShiftTotals: LastShiftTotalsResponse | null,
  ): Promise<boolean> {
    if (!state) {
      throw new Error('Tax register state is required.');
    }
    if (!lastShiftTotals) {
      lastShiftTotals = {
        UID: null,
        ShiftState: Number(state.ShiftState) === 1 ? 1 : 0,
        ZRepPresent: !!state.ZRepPresent,
        Testing: !!state.Testing,
        Totals: null,
      } as LastShiftTotalsResponse;
    }

    const storageKey = this._getStorageKey(taxRegisterId);
    const storedData =
      ((await this._companyStorage.get(
        storageKey,
      )) as TaxRegisterStoredData | null) || {};

    if (!storedData.state) {
      await this._companyStorage.set(storageKey, {
        state,
        lastShiftTotals,
      } as TaxRegisterStoredData);
      await this._companyStorage.set(
        this._getDocumentsStorageKey(taxRegisterId),
        [],
      );
      return true;
    }

    const currentState = this._calculateTaxRegisterState(
      taxRegisterId,
      storedData,
      await this.getDocuments(taxRegisterId),
    );
    const currentShiftIsOnline =
      !currentState.OfflineSession && !currentState.Offline;
    const serverShiftIsOnline = !isOfflineSequenceActive(
      state.OfflineNextLocalNum,
    );
    const localNumbersMatch =
      Number(currentState.NextLocalNum) === Number(state.NextLocalNum);

    if (!currentShiftIsOnline || !serverShiftIsOnline || !localNumbersMatch) {
      return false;
    }

    await this._companyStorage.set(storageKey, {
      state,
      lastShiftTotals,
    } as TaxRegisterStoredData);
    await this._companyStorage.set(
      this._getDocumentsStorageKey(taxRegisterId),
      [],
    );
    return true;
  }

  private _calculateTaxRegisterState(
    taxRegisterId: TaxRegisterId,
    storedData: TaxRegisterStoredData | null,
    documents: string[],
  ): TaxRegisterState {
    const initialState = storedData?.state;
    if (!initialState) {
      throw new Error(
        `Tax register state not found. TaxRegister fiscal number ${taxRegisterId}`,
      );
    }

    const state = JSON.parse(JSON.stringify(initialState)) as TaxRegisterState;
    const lastShiftTotals = storedData.lastShiftTotals;
    if (lastShiftTotals) {
      state.ShiftState = lastShiftTotals.ShiftState;
      state.ZRepPresent = lastShiftTotals.ZRepPresent;
      state.Testing = lastShiftTotals.Testing;
    }

    state.OfflineSession = isOfflineSequenceActive(state.OfflineNextLocalNum);
    state.Offline = state.OfflineSession;

    if (!documents.length) {
      return state;
    }

    const lastDocument = this._parseDocument(documents[documents.length - 1]);
    const savedNextLocalNumber = Number(state.NextLocalNum);
    const documentNextLocalNumber = Number(lastDocument.head.ORDERNUM) + 1;
    state.NextLocalNum = Number.isInteger(savedNextLocalNumber)
      ? Math.max(savedNextLocalNumber, documentNextLocalNumber)
      : documentNextLocalNumber;

    const lastDocuments = documents
      .slice(-2)
      .map((document) => this._parseDocument(document));
    for (const document of lastDocuments) {
      const head = document.head;
      if (!head.ORDERTAXNUM) {
        continue;
      }

      const isOffline = this._toBoolean(head.OFFLINE);
      state.Offline = isOffline;
      state.OfflineSession = isOffline;
      if (head.TESTING !== undefined) {
        state.Testing = this._toBoolean(head.TESTING);
      }

      const offlineSequentialNumber = this._getOfflineSequentialNumber(
        head.ORDERTAXNUM,
      );
      if (offlineSequentialNumber !== null) {
        state.OfflineNextLocalNum = (offlineSequentialNumber + 1).toString();
      }

      if (document.isZReport) {
        state.ZRepPresent = true;
        state.ShiftState = 1;
        continue;
      }

      const documentType = Number(head.DOCTYPE);
      if (documentType === 102) {
        state.OfflineSession = true;
        state.Offline = true;
      } else if (documentType === 103) {
        state.OfflineSession = false;
        state.Offline = true;
        state.OfflineNextLocalNum = '1';
      } else if (documentType === 101) {
        state.ShiftState = 0;
        state.ZRepPresent = true;
      } else {
        state.ShiftState = 1;
        state.ZRepPresent = false;
      }
    }

    return state;
  }

  async saveLastShiftTotals(
    taxRegisterId: TaxRegisterId,
    lastShiftTotals: LastShiftTotalsResponse,
  ): Promise<void> {
    return this._saveTaxRegisterData(taxRegisterId, { lastShiftTotals });
  }

  async getLastShiftTotals(
    taxRegisterId: TaxRegisterId,
  ): Promise<LastShiftTotalsResponse | null> {
    const data = await this._getTaxRegisterData(taxRegisterId);
    return data?.lastShiftTotals || null;
  }

  async saveDocument(
    taxRegisterId: TaxRegisterId,
    document: string,
  ): Promise<void> {
    this._getDocumentLocalNumber(document);

    const documents = await this.getDocuments(taxRegisterId);
    documents.push(document);
    await this._companyStorage.set(
      this._getDocumentsStorageKey(taxRegisterId),
      documents,
    );
  }

  async getDocuments(taxRegisterId: TaxRegisterId): Promise<string[]> {
    return (
      (await this._companyStorage.get(
        this._getDocumentsStorageKey(taxRegisterId),
      )) || []
    );
  }

  async getNextLocalNumber(taxRegisterId: TaxRegisterId): Promise<number> {
    const documents = await this.getDocuments(taxRegisterId);
    const documentNextLocalNumber = documents.length
      ? this._getDocumentLocalNumber(documents[documents.length - 1]) + 1
      : null;
    const state = await this.getState(taxRegisterId);
    const stateNextLocalNumber = Number(state?.NextLocalNum);
    const hasStateNextLocalNumber =
      Number.isInteger(stateNextLocalNumber) && stateNextLocalNumber > 0;

    if (documentNextLocalNumber === null && !hasStateNextLocalNumber) {
      throw new Error(
        `Next local number not found. TaxRegister fiscal number ${taxRegisterId}`,
      );
    }

    return hasStateNextLocalNumber
      ? Math.max(documentNextLocalNumber || 0, stateNextLocalNumber)
      : documentNextLocalNumber;
  }

  private async _saveTaxRegisterData(
    taxRegisterId: TaxRegisterId,
    data: Partial<TaxRegisterStoredData>,
  ): Promise<void> {
    const storageKey = this._getStorageKey(taxRegisterId);
    const storedData = await this._companyStorage.get(storageKey);
    const dataToStore: TaxRegisterStoredData = {
      ...(storedData || {}),
      ...data,
    };

    await this._companyStorage.set(storageKey, dataToStore);
  }

  private async _getTaxRegisterData(
    taxRegisterId: TaxRegisterId,
  ): Promise<TaxRegisterStoredData | null> {
    const storageKey = this._getStorageKey(taxRegisterId);
    return (await this._companyStorage.get(storageKey)) || null;
  }

  private _getStorageKey(taxRegisterId: TaxRegisterId): string {
    const id = this._normalizeTaxRegisterId(taxRegisterId);
    return `${TAX_REGISTER_DATA_KEY_PREFIX}${id}`;
  }

  private _getDocumentsStorageKey(taxRegisterId: TaxRegisterId): string {
    const id = this._normalizeTaxRegisterId(taxRegisterId);
    return `${TAX_REGISTER_DOCUMENTS_KEY_PREFIX}${id}`;
  }

  private _getServerStateStorageKey(taxRegisterId: TaxRegisterId): string {
    const id = this._normalizeTaxRegisterId(taxRegisterId);
    return `${TAX_REGISTER_SERVER_STATE_KEY_PREFIX}${id}`;
  }

  private _getDocumentLocalNumber(document: string): number {
    const parsedDocument = this._parseDocument(document);
    const localNumber = Number(parsedDocument.head.ORDERNUM);

    if (!Number.isInteger(localNumber) || localNumber < 0) {
      throw new Error('Tax document ORDERNUM is invalid.');
    }

    return localNumber;
  }

  private _parseDocument(document: string): {
    head: TaxDocumentHead;
    isZReport: boolean;
  } {
    const xmlDocument = this._getXmlDocument(document);
    const parsedDocument = xml2json<ParsedTaxDocument>(xmlDocument);
    const head =
      parsedDocument?.CHECK?.CHECKHEAD || parsedDocument?.ZREP?.ZREPHEAD;

    if (!head) {
      throw new Error('Tax document CHECKHEAD or ZREPHEAD is missing.');
    }

    const localNumber = Number(head.ORDERNUM);
    if (!Number.isInteger(localNumber) || localNumber < 0) {
      throw new Error('Tax document ORDERNUM is invalid.');
    }

    return {
      head,
      isZReport: !!parsedDocument.ZREP,
    };
  }

  private _getOfflineSequentialNumber(orderTaxNumber: string): number | null {
    const parts = orderTaxNumber.toString().split('.');
    if (parts.length !== 3) {
      return null;
    }

    const offlineSequentialNumber = Number(parts[1]);
    return Number.isInteger(offlineSequentialNumber)
      ? offlineSequentialNumber
      : null;
  }

  private _toBoolean(value: boolean | string): boolean {
    return value === true || value === 'true';
  }

  private _getXmlDocument(document: string): string {
    if (!document || !document.trim()) {
      throw new Error('Tax document is required.');
    }

    const value = document.trim();
    if (value.startsWith('<')) {
      return value;
    }

    const base64Value = value.startsWith('data:')
      ? value.substring(value.indexOf(',') + 1)
      : value;

    try {
      const decodedValue = window.atob(base64Value).trim();
      if (decodedValue.startsWith('<')) {
        return decodedValue;
      }
    } catch {
      // The validation error below is more useful than the atob error.
    }

    throw new Error('Tax document must be XML or base64-encoded XML.');
  }

  private _normalizeTaxRegisterId(taxRegisterId: TaxRegisterId): string {
    if (taxRegisterId === null || taxRegisterId === undefined) {
      throw new Error('Tax register id is required.');
    }

    const id = taxRegisterId.toString().trim();
    if (!id) {
      throw new Error('Tax register id is required.');
    }

    return id;
  }

  private _getTimestamp(
    value: Date | string | number,
    valueName: string,
  ): number {
    const timestamp =
      value instanceof Date ? value.getTime() : new Date(value).getTime();
    if (Number.isNaN(timestamp)) {
      throw new Error(`${valueName} is invalid.`);
    }

    return timestamp;
  }
}
